import {getFirebaseToken} from '../components/common/firebase';
import {BACKEND_BASE_URL} from './apiClient';

// Derive the WebSocket endpoint from the single backend base URL
// (https -> wss, http -> ws) so there's no second host definition.
const TRANSCRIPTION_WS_URL = `${BACKEND_BASE_URL.replace(/^http/, 'ws')}/ws/soniox`;

// Supported languages for speech-to-text
const LANGUAGE_TO_ISO: Record<string, string> = {
  Afrikaans: 'af',
  Albanian: 'sq',
  Arabic: 'ar',
  Azerbaijani: 'az',
  Basque: 'eu',
  Belarusian: 'be',
  Bengali: 'bn',
  Bosnian: 'bs',
  Bulgarian: 'bg',
  Catalan: 'ca',
  Chinese: 'zh',
  Croatian: 'hr',
  Czech: 'cs',
  Danish: 'da',
  Dutch: 'nl',
  English: 'en',
  Estonian: 'et',
  Finnish: 'fi',
  French: 'fr',
  Galician: 'gl',
  German: 'de',
  Greek: 'el',
  Gujarati: 'gu',
  Hebrew: 'he',
  Hindi: 'hi',
  Hungarian: 'hu',
  Indonesian: 'id',
  Italian: 'it',
  Japanese: 'ja',
  Kannada: 'kn',
  Kazakh: 'kk',
  Korean: 'ko',
  Latvian: 'lv',
  Lithuanian: 'lt',
  Macedonian: 'mk',
  Malay: 'ms',
  Malayalam: 'ml',
  Marathi: 'mr',
  Norwegian: 'no',
  Persian: 'fa',
  Polish: 'pl',
  Portuguese: 'pt',
  Punjabi: 'pa',
  Romanian: 'ro',
  Russian: 'ru',
  Serbian: 'sr',
  Slovak: 'sk',
  Slovenian: 'sl',
  Somali: 'so',
  Spanish: 'es',
  Swahili: 'sw',
  Swedish: 'sv',
  Tagalog: 'tl',
  Tamil: 'ta',
  Telugu: 'te',
  Thai: 'th',
  Turkish: 'tr',
  Ukrainian: 'uk',
  Urdu: 'ur',
  Vietnamese: 'vi',
  Welsh: 'cy',
};

// Common languages first (ordered by US translation frequency), then the rest alphabetical.
// English excluded since it's always the base language for live translation.
const COMMON_ORDER = [
  'Spanish',
  'Chinese',
  'Vietnamese',
  'Korean',
  'Arabic',
  'French',
  'Russian',
  'German',
  'Japanese',
  'Portuguese',
  'Italian',
  'Hindi',
  'Bengali',
  'Urdu',
  'Indonesian',
  'Turkish',
  'Thai',
  'Polish',
  'Ukrainian',
  'Dutch',
  'Greek',
  'Czech',
  'Romanian',
  'Hungarian',
];
const COMMON_SET = new Set(COMMON_ORDER);

export const SONIOX_SUPPORTED_LANGUAGES = [
  ...COMMON_ORDER,
  ...Object.keys(LANGUAGE_TO_ISO)
    .filter(lang => lang !== 'English' && !COMMON_SET.has(lang))
    .sort(),
];
/**
 * Translates a display language name (e.g. "Spanish") to an ISO code (e.g. "es").
 * Returns null when no mapping exists.
 */
export function languageToISO(languageName: any) {
  if (!languageName) return null;
  return LANGUAGE_TO_ISO[languageName] || null;
}

// Send a small silent PCM frame as a keep-alive when no mic audio is flowing
// (e.g. the client paused or the worklet momentarily stalled). 160 samples of
// Int16 silence = 320 bytes, which clears the 64-byte sendAudio filter and
// keeps the upstream Soniox idle timer from firing.
const KEEPALIVE_SILENCE = new ArrayBuffer(320);

class SonioxTranscriptionService {
  // TODO(ts): replace the index signature with real field declarations.
  [key: string]: any;

  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.onTranscriptCallback = null;
    this.onErrorCallback = null;
    this.onStatusChange = null;
    this.connectionAttempts = 0;
    // Unbounded retries; we never give up on an active session because it is
    // far better to reconnect 10 minutes later than to silently drop audio
    // forever when the tab comes back to the foreground.
    this.maxReconnectDelayMs = 30_000;
    this.intentionalDisconnect = false;

    // Persisted across reconnects so we can re-use the same config
    this._connectOptions = null;
    // True once at least one audio binary frame has been sent
    this._hasSentAudio = false;
    // Timestamp of the last binary audio frame we sent upstream. Used by
    // session managers to decide whether to push a keepalive.
    this._lastAudioSentAt = 0;

    // ---------- Reconnect / audio-buffering state ----------
    // While the socket is down we buffer captured PCM here and replay it in
    // order the moment we reconnect, so a dropped connection never loses the
    // slice of the visit that happened during the outage. Bounded to ~5 min
    // of 16 kHz mono s16le audio as a memory backstop — real outages (server
    // deploys, Wi-Fi blips) are seconds long.
    this._pendingAudio = [];
    this._pendingBytes = 0;
    this._maxPendingBytes = 16000 * 2 * 300; // ~5 minutes
    this._droppedBufferedBytes = 0;
    // True ONLY while a connect() attempt is actively in flight. Gates
    // ensureConnected() so we never open two sockets at once. Crucially this
    // is NOT held during the backoff wait (that is `_nextRetryAt`), so a
    // throttled background setTimeout can never pin us in a non-retrying
    // state — the audio-clock driver can always make progress.
    this._reconnecting = false;
    // Earliest wall-clock time (ms) at which the next reconnect may start.
    this._nextRetryAt = 0;
    // Set when the server rejects us with a non-recoverable close code
    // (1008/1003/4000). Stops all further retry attempts.
    this._permanentFailure = false;

    // ---------- Token accumulation ----------
    // Final tokens are emitted exactly once and never change.
    this.finalTokens = [];
    // Track non-final count to detect endpoint transitions.
    this._prevNonFinalCount = 0;

    // ---------- Speaker label mapping ----------
    // Speaker labels (e.g. "1", "2") → our 0-indexed IDs (0–3)
    this._speakerLabelMap = new Map();
    this._nextSpeakerIdx = 0;
    this._maxSpeakers = 4;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Opens a WebSocket to the transcription proxy and sends the initial config.
   *
   * @param onTranscriptCallback - called with per-speaker update data
   * @param onErrorCallback      - called on errors
   * @param options
   * @param options.languageHints - ISO codes, e.g. ['en','es']
   * @param [options.translation] - Translation config
   * @param [options.onFinished]  - called when session finishes
   */
  async connect(
    onTranscriptCallback: any,
    onErrorCallback: any,
    options: Record<string, any> = {},
  ) {
    this.intentionalDisconnect = false;
    this._hasSentAudio = false;
    this.onTranscriptCallback = onTranscriptCallback;
    this.onErrorCallback = onErrorCallback;
    if (options.onStatusChange) this.onStatusChange = options.onStatusChange;
    this._connectOptions = options;

    this._emitStatus('connecting');

    if (this.socket) {
      const oldSock = this.socket;
      this.socket = null;
      oldSock.onclose = null;
      oldSock.onerror = null;
      oldSock.onopen = null;
      oldSock.onmessage = null;
      try {
        oldSock.close();
      } catch (_) {
        /* ignore */
      }
      this.isConnected = false;
    }

    // The token fetch is the one leg of connect() that runs before the
    // socket's own 10s connection timeout exists, so bound it here — a hung
    // fetch would otherwise leave connect() pending forever.
    let tokenTimeout: any = null;
    let firebaseToken: string;
    try {
      firebaseToken = await Promise.race([
        getFirebaseToken(),
        new Promise<never>((_, reject) => {
          tokenTimeout = setTimeout(() => {
            reject(new Error('Timed out fetching the transcription token'));
          }, 10_000);
        }),
      ]);
    } finally {
      clearTimeout(tokenTimeout);
    }

    // disconnect() may have been called while the token fetch was in flight
    // (dictation cancelled, connect watchdog fired). Opening the socket now
    // would leak a connection that nothing owns or will ever close.
    if (this.intentionalDisconnect) {
      throw new Error('Transcription connection cancelled');
    }

    const wsUrl = `${TRANSCRIPTION_WS_URL}?token=${encodeURIComponent(firebaseToken)}`;

    return new Promise((resolve, reject) => {
      let settled = false;

      const connectionTimeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        const err = new Error('Transcription service connection timed out');
        console.error('[Transcription]', err.message);

        // Tear down the half-open socket and arm the next attempt. We do the
        // scheduling here (rather than letting onclose do it) because a stuck
        // CONNECTING socket may never fire onclose.
        const sock = this.socket;
        if (sock) {
          sock.onopen = null;
          sock.onclose = null;
          sock.onerror = null;
          sock.onmessage = null;
          try {
            sock.close();
          } catch (_) {
            /* ignore */
          }
        }
        this.socket = null;
        this.isConnected = false;
        this._reconnecting = false;
        if (!this.intentionalDisconnect && !this._permanentFailure) {
          this._emitStatus('reconnecting');
          this._nextRetryAt = Date.now() + this._backoffDelay();
          this._scheduleReconnectFallback();
        }
        if (this.onErrorCallback) this.onErrorCallback(err);
        reject(err);
      }, 10000);

      try {
        this.socket = new WebSocket(wsUrl);
        this.socket.binaryType = 'arraybuffer';
      } catch (error) {
        clearTimeout(connectionTimeout);
        console.error('[Transcription] Connection error', error);
        if (this.onErrorCallback) this.onErrorCallback(error);
        throw error;
      }

      this.socket.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[Transcription] WebSocket connected');

        const config: Record<string, any> = {
          model: 'stt-rt-v4',
          audio_format: 'pcm_s16le',
          sample_rate: 16000,
          num_channels: 1,
          enable_endpoint_detection: true,
          enable_speaker_diarization:
            options.enableSpeakerDiarization !== false,
          enable_language_identification: true,
        };

        // Only include `language_hints` when the caller actually provided
        // a non-empty list. Omitting the field lets Soniox auto-detect any
        // supported language — which is what Scribe wants so providers can
        // speak whatever language they like without pre-configuring.
        if (
          Array.isArray(options.languageHints) &&
          options.languageHints.length > 0
        ) {
          config.language_hints = options.languageHints;
        }

        if (options.translation) {
          config.translation = options.translation;
        }

        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(config));
          console.log('[Transcription] Config sent', {
            hasLanguageHints: !!config.language_hints,
            hasTranslation: !!config.translation,
          });
        }

        // Set isConnected AFTER the config is queued so sendAudio() can
        // never push binary data ahead of the JSON config message.
        this.isConnected = true;
        this.connectionAttempts = 0;
        this._reconnecting = false;
        this._nextRetryAt = 0;
        this._emitStatus('connected');

        // Replay any audio captured while we were disconnected so a dropped
        // socket never loses a slice of the visit. Runs before the first live
        // frame because sendAudio() also drains the backlog first.
        this._flushPendingAudio();

        if (!settled) {
          settled = true;
          resolve(true);
        }
      };

      this.socket.onmessage = (event: any) => {
        this._handleMessage(event);
      };

      this.socket.onerror = (error: any) => {
        console.error('[Transcription] WebSocket error', error);
        this.isConnected = false;
        if (this.onErrorCallback) {
          this.onErrorCallback(error);
        }
      };

      this.socket.onclose = (event: any) => {
        clearTimeout(connectionTimeout);
        console.log(
          '[Transcription] WebSocket closed',
          event.code,
          event.reason,
        );
        this.isConnected = false;
        this._reconnecting = false;

        // Non-recoverable server rejections — do NOT retry
        if (event.code === 1008 || event.code === 1003 || event.code === 4000) {
          const reason = event.reason || 'Policy violation';
          console.error(
            `[Transcription] Connection rejected (${event.code}): ${reason}`,
          );
          const serverErr = new Error(
            `Transcription connection rejected: ${reason}`,
          );
          this._permanentFailure = true;
          this._emitStatus('failed');
          if (this.onErrorCallback) this.onErrorCallback(serverErr);
          if (!settled) {
            settled = true;
            reject(serverErr);
          }
          return;
        }

        if (!this.intentionalDisconnect) {
          // Arm the next attempt. The retry is driven primarily by
          // ensureConnected() off the audio clock (which keeps ticking even
          // when the tab is backgrounded and setTimeout is throttled to
          // ~1/min); this setTimeout is only the foreground / no-audio
          // fallback. Audio captured during the gap is buffered and replayed
          // on reconnect, so nothing is lost.
          this._emitStatus('reconnecting');
          this._nextRetryAt = Date.now() + this._backoffDelay();
          this._scheduleReconnectFallback();
        }

        if (!settled) {
          settled = true;
          reject(
            new Error(
              `Transcription service closed before connecting (code ${event.code})`,
            ),
          );
        }
      };
    });
  }

  /**
   * Send raw PCM audio data (ArrayBuffer) to the transcription service.
   */
  sendAudio(audioData: any) {
    if (
      !audioData ||
      !(audioData instanceof ArrayBuffer) ||
      audioData.byteLength < 64
    ) {
      return;
    }
    if (this.intentionalDisconnect) return;

    // Socket is down (mid-reconnect): hold the audio so we can replay it in
    // order the moment we reconnect, instead of dropping the slice of the
    // visit that happened during the outage.
    if (
      !this.socket ||
      !this.isConnected ||
      this.socket.readyState !== WebSocket.OPEN
    ) {
      this._bufferAudio(audioData);
      return;
    }

    // Drain any backlog first so replayed audio stays chronologically ahead
    // of this live frame.
    if (this._pendingAudio.length) {
      this._flushPendingAudio();
    }

    this._hasSentAudio = true;
    this._lastAudioSentAt = Date.now();
    this.socket.send(audioData);
  }

  /**
   * Send a tiny silent PCM frame to keep the upstream session from timing
   * out when no mic audio is flowing (client paused, worklet stalled, etc.).
   * Safe to call on any schedule — it is a no-op while disconnected.
   */
  sendKeepAlive() {
    if (
      !this.socket ||
      !this.isConnected ||
      this.socket.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    try {
      this._hasSentAudio = true;
      this._lastAudioSentAt = Date.now();
      this.socket.send(KEEPALIVE_SILENCE);
    } catch (_) {
      /* ignore transient send errors */
    }
  }

  /**
   * Force-finalize all pending non-final tokens.
   */
  finalize() {
    if (
      this.socket &&
      this.isConnected &&
      this.socket.readyState === WebSocket.OPEN
    ) {
      this.socket.send(JSON.stringify({type: 'finalize'}));
    }
  }

  /**
   * Gracefully end the session and close the WebSocket.
   */
  disconnect() {
    this.intentionalDisconnect = true;

    if (this.socket) {
      const sock = this.socket;
      this.socket = null;

      // Detach handlers BEFORE closing so the async onclose event
      // cannot trigger _handleReconnection after an intentional disconnect.
      sock.onclose = null;
      sock.onerror = null;
      sock.onopen = null;
      sock.onmessage = null;

      try {
        if (sock.readyState === WebSocket.OPEN && this._hasSentAudio) {
          sock.send('');
        }
      } catch (_) {
        /* ignore */
      }

      try {
        sock.close();
      } catch (_) {
        /* ignore */
      }
    }

    this.isConnected = false;
    this._hasSentAudio = false;
    this.connectionAttempts = 0;
    this._reconnecting = false;
    this._nextRetryAt = 0;
    this._pendingAudio = [];
    this._pendingBytes = 0;
    this._emitStatus('disconnected');
    this._resetTokenState();
  }

  /**
   * Reset accumulated token state (used on language change / reconnect).
   */
  resetTokenState() {
    this._resetTokenState();
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      hasApiKey: true,
      connectionAttempts: this.connectionAttempts,
      provider: 'transcription',
      model: 'stt-rt-v4',
    };
  }

  // ---------------------------------------------------------------------------
  // Token processing
  // ---------------------------------------------------------------------------

  _handleMessage(event: any) {
    try {
      let jsonStr;
      if (typeof event.data === 'string') {
        jsonStr = event.data;
      } else if (event.data instanceof ArrayBuffer) {
        jsonStr = new TextDecoder().decode(event.data);
      } else if (event.data instanceof Blob) {
        // Blob needs async read — shouldn't happen with binaryType='arraybuffer'
        event.data.text().then((text: any) => {
          this._handleMessage({data: text});
        });
        return;
      } else {
        console.warn(
          '[Transcription] Unknown message type:',
          typeof event.data,
        );
        return;
      }

      const response = JSON.parse(jsonStr);

      // First successful message proves the session is healthy.
      if (this.connectionAttempts > 0) {
        this.connectionAttempts = 0;
      }

      if (response.error_code) {
        console.error(
          `[Transcription] Error ${response.error_code}: ${response.error_message}`,
        );
        if (this.onErrorCallback) {
          this.onErrorCallback(
            new Error(
              `Transcription error ${response.error_code}: ${response.error_message}`,
            ),
          );
        }
        return;
      }

      const newFinalTokens = [];
      const nonFinalTokens = [];

      for (const token of response.tokens || []) {
        if (!token.text) continue;
        // The service emits angle-bracketed control tokens (e.g. "<end>" for
        // endpoint detection, "<fin>" when the stream is being finalized,
        // sometimes speaker tags) that are NOT real speech and must not
        // appear in the rendered transcript. We drop any token whose trimmed
        // text is a single <...> marker, then also catch a couple of known
        // unbracketed variants like ",end." that show up in some sessions.
        const trimmedText = token.text.trim();
        if (/^<[^>]*>$/.test(trimmedText)) continue;
        const stripped = token.text.replace(/[,.\s]/g, '').toLowerCase();
        if (stripped === 'end' || stripped === 'fin') continue;
        if (/^\s*,?\s*end\s*\.?\s*$/i.test(token.text)) continue;
        if (token.is_final) {
          this.finalTokens.push(token);
          newFinalTokens.push(token);
        } else {
          nonFinalTokens.push(token);
        }
      }

      // Endpoint heuristic: we had provisional tokens before and now everything
      // in this message is final (no new non-finals). This indicates the service
      // finalized the utterance via endpoint detection.
      const isEndpoint =
        this._prevNonFinalCount > 0 &&
        nonFinalTokens.length === 0 &&
        newFinalTokens.length > 0;
      this._prevNonFinalCount = nonFinalTokens.length;

      const speakerData = this._buildSpeakerData(
        newFinalTokens,
        nonFinalTokens,
        isEndpoint,
      );
      speakerData.finished = Boolean(response.finished);

      if (this.onTranscriptCallback) {
        this.onTranscriptCallback(speakerData);
      }

      if (response.finished && this._connectOptions?.onFinished) {
        this._connectOptions.onFinished();
      }
    } catch (error) {
      console.error('[Transcription] Message parse error', error);
    }
  }

  /**
   * Converts token arrays into a per-speaker structure suitable for
   * GroupTranscriptionManager consumption.
   *
   * Return shape:
   * {
   *   speakers: [{
   *     speaker: <0-indexed id>,
   *     finalText: <newly confirmed transcription text>,
   *     interimText: <current provisional transcription text>,
   *     finalTranslation: <newly confirmed translation text>,
   *     interimTranslation: <current provisional translation text>,
   *     language: <ISO code>,
   *     confidence: <0–1>,
   *     is_final: <bool>,
   *   }],
   *   is_final: <bool>,
   *   speech_final: <bool>,
   *   finished: false,
   *   transcript: <combined text>,
   *   confidence: <best confidence>,
   * }
   */
  _buildSpeakerData(newFinalTokens: any, nonFinalTokens: any, isEndpoint: any) {
    const finalsBySpeaker = new Map();
    const interimsBySpeaker = new Map();

    // --- Group new final tokens by speaker ---
    for (const token of newFinalTokens) {
      const id = this._mapSpeakerId(token.speaker);
      if (!finalsBySpeaker.has(id)) {
        finalsBySpeaker.set(id, {
          transcription: '',
          translation: '',
          language: null,
          confidence: 0,
          startMs: null,
          endMs: null,
        });
      }
      const entry = finalsBySpeaker.get(id);

      if (token.translation_status === 'translation') {
        entry.translation += token.text;
      } else {
        entry.transcription += token.text;
        if (token.language) entry.language = token.language;
      }
      if (token.confidence != null)
        entry.confidence = Math.max(entry.confidence, token.confidence);
      if (token.start_ms != null && entry.startMs === null)
        entry.startMs = token.start_ms;
      if (token.end_ms != null) entry.endMs = token.end_ms;
    }

    // --- Group non-final (provisional) tokens by speaker ---
    for (const token of nonFinalTokens) {
      const id = this._mapSpeakerId(token.speaker);
      if (!interimsBySpeaker.has(id)) {
        interimsBySpeaker.set(id, {
          transcription: '',
          translation: '',
          language: null,
        });
      }
      const entry = interimsBySpeaker.get(id);

      if (token.translation_status === 'translation') {
        entry.translation += token.text;
      } else {
        entry.transcription += token.text;
        if (token.language) entry.language = token.language;
      }
    }

    // --- Merge into a unified speaker list ---
    const allSpeakerIds = new Set([
      ...finalsBySpeaker.keys(),
      ...interimsBySpeaker.keys(),
    ]);
    const speakers = [];

    for (const id of allSpeakerIds) {
      const f = finalsBySpeaker.get(id);
      const p = interimsBySpeaker.get(id);

      speakers.push({
        speaker: id,
        finalText: f?.transcription?.trim() || '',
        interimText: p?.transcription?.trim() || '',
        finalTranslation: f?.translation?.trim() || '',
        interimTranslation: p?.translation?.trim() || '',
        language: f?.language || p?.language || null,
        confidence: f?.confidence || 0.5,
        is_final: isEndpoint && !p,
        startMs: f?.startMs ?? null,
        endMs: f?.endMs ?? null,
      });
    }

    return {
      speakers,
      is_final: isEndpoint,
      speech_final: isEndpoint,
      finished: false,
      transcript: speakers
        .map(s => (s.finalText + ' ' + s.interimText).trim())
        .join(' ')
        .trim(),
      confidence:
        speakers.length > 0 ? Math.max(...speakers.map(s => s.confidence)) : 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Speaker label mapping
  // ---------------------------------------------------------------------------

  /**
   * Maps a speaker label (e.g. "1", 2, undefined) to a stable
   * 0-indexed integer bounded by _maxSpeakers.
   */
  _mapSpeakerId(label: any) {
    if (label == null) return 0;
    const key = String(label);
    if (this._speakerLabelMap.has(key)) {
      return this._speakerLabelMap.get(key);
    }
    const idx = Math.min(this._nextSpeakerIdx, this._maxSpeakers - 1);
    this._speakerLabelMap.set(key, idx);
    if (this._nextSpeakerIdx < this._maxSpeakers) {
      this._nextSpeakerIdx++;
    }
    // New speaker mapped to slot
    return idx;
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  _resetTokenState() {
    this.finalTokens = [];
    this._prevNonFinalCount = 0;
    this._speakerLabelMap.clear();
    this._nextSpeakerIdx = 0;
  }

  /**
   * Idempotent connection guard — safe to call extremely frequently.
   *
   * The scribe session manager calls this from the AudioWorklet audio
   * callback, which keeps firing even when the tab is backgrounded and
   * main-thread timers are throttled to ~1/min. That makes it the most
   * reliable trigger for re-establishing a dropped socket: the next frame of
   * audio produced after a drop kicks off a reconnect (subject to the
   * `_nextRetryAt` backoff window). It opens at most one socket at a time.
   */
  ensureConnected() {
    if (this.intentionalDisconnect || this._permanentFailure) return;
    if (this.isConnected || this._reconnecting) return;
    if (Date.now() < this._nextRetryAt) return;
    // Never connected yet — startSession owns the first connect().
    if (!this._connectOptions) return;

    this._reconnecting = true;
    this.connectionAttempts++;
    console.log(
      `[Transcription] Reconnecting (attempt ${this.connectionAttempts})`,
    );
    this.connect(
      this.onTranscriptCallback,
      this.onErrorCallback,
      this._connectOptions,
    ).catch(err => {
      console.warn(
        '[Transcription] Reconnect attempt failed',
        err?.message || err,
      );
      // Normally onclose / onerror / the connection timeout already reset
      // `_reconnecting` and armed the next backoff window. But if connect()
      // threw before any socket lifecycle event could fire (e.g. the
      // Firebase token fetch failed), recover the retry loop here so the
      // audio-clock driver can try again.
      if (!this.isConnected && this._reconnecting) {
        this._reconnecting = false;
        if (!this.intentionalDisconnect && !this._permanentFailure) {
          this._nextRetryAt = Date.now() + this._backoffDelay();
          this._scheduleReconnectFallback();
        }
      }
    });
  }

  /**
   * Exponential backoff with full jitter, capped at `maxReconnectDelayMs`.
   * The attempt count is never capped — a long visit may span many minutes
   * of network trouble and dropping the audio forever is far worse than
   * continuing to retry.
   */
  _backoffDelay() {
    const base = Math.min(
      this.maxReconnectDelayMs,
      500 * 2 ** Math.min(this.connectionAttempts, 6),
    );
    return Math.floor(Math.random() * base);
  }

  /**
   * Foreground / no-audio fallback that nudges ensureConnected() after the
   * backoff window. In a backgrounded tab this setTimeout may be throttled,
   * but that's fine — the audio-clock driver covers that case. ensureConnected
   * is idempotent, so an extra call here is harmless.
   */
  _scheduleReconnectFallback() {
    const wait = Math.max(0, this._nextRetryAt - Date.now()) + 50;
    setTimeout(() => {
      try {
        this.ensureConnected();
      } catch (_) {
        /* ignore */
      }
    }, wait);
  }

  _bufferAudio(buf: any) {
    this._pendingAudio.push(buf);
    this._pendingBytes += buf.byteLength;
    // Bound memory. Real outages are seconds long, so this cap is just a
    // backstop; if we ever exceed it we drop the OLDEST audio first (least
    // useful) and tally the loss.
    while (
      this._pendingBytes > this._maxPendingBytes &&
      this._pendingAudio.length > 1
    ) {
      const dropped = this._pendingAudio.shift();
      this._pendingBytes -= dropped.byteLength;
      this._droppedBufferedBytes += dropped.byteLength;
    }
  }

  _flushPendingAudio() {
    if (!this._pendingAudio.length) return;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const backlog = this._pendingAudio;
    this._pendingAudio = [];
    this._pendingBytes = 0;

    let flushed = 0;
    for (const buf of backlog) {
      try {
        this.socket.send(buf);
        flushed += buf.byteLength;
      } catch (_) {
        // Socket went away mid-flush — re-buffer the remainder so we can try
        // again on the next reconnect rather than losing it.
        const idx = backlog.indexOf(buf);
        if (idx >= 0) {
          const remainder = backlog.slice(idx);
          this._pendingAudio = remainder.concat(this._pendingAudio);
          this._pendingBytes =
            remainder.reduce((n: any, b: any) => n + b.byteLength, 0) +
            this._pendingBytes;
        }
        break;
      }
    }
    if (flushed > 0) {
      this._hasSentAudio = true;
      this._lastAudioSentAt = Date.now();
      console.log(
        `[Transcription] Replayed ${flushed} bytes of buffered audio after reconnect`,
      );
    }
  }

  _emitStatus(status: any) {
    try {
      this.onStatusChange?.(status);
    } catch (_) {
      /* ignore callback failures */
    }
  }
}

export default SonioxTranscriptionService;
